/**
 * ⚖ The S3 composition gate, on the promotion path (§4-ter).
 *
 * Sits between "Set active" and the label move for a `harn:sp-<name>` persona.
 * Clicking promote on a specialist SELECTS a candidate rather than promoting it:
 * the gate loads that triple's recorded verdict and only an affirmative `pass`
 * exposes the button that actually moves the label. Every other state blocks,
 * including the two that look harmless — could-not-read and not-yet-checked.
 *
 * ⚠⚠ THE ONE RULE THIS FILE EXISTS TO ENFORCE IN THE UI: **the full check
 * RECORDS a verdict, and a recorded `pass` is what the gate consults.** So
 * running it is minting an approval. It fires ONLY from a button an operator
 * presses after reading what it does — never on mount, never on selection,
 * never as a retry, never as a "refresh". Tier A is the safe one (structural,
 * deterministic, non-persisting) and is offered separately for exactly that
 * reason: an author can clear structural problems without manufacturing an
 * approval for text nobody reviewed.
 */

import { AlertTriangle, CheckCircle2, HelpCircle, Loader2, PowerOff, ShieldAlert } from "lucide-react";
import { useCallback, useEffect, useState } from "react";

import {
  blockReason,
  blocksPromotion,
  judgeRequestFailureNotice,
  requireBaseVersion,
  selectGateState,
  specialistIdFromSlug,
  type GateState,
} from "@/lib/composition-gate";
import {
  describeSpecialistsError,
  getCompositionVerdict,
  runCompositionCheck,
  runTierA,
  SpecialistsApiError,
  type TierAResult,
} from "@/lib/specialists-api";

function errShape(e: unknown): { ok: false; code?: string; error: string } {
  if (e instanceof SpecialistsApiError) {
    return { ok: false, code: e.code, error: describeSpecialistsError(e) };
  }
  return { ok: false, error: e instanceof Error ? e.message : String(e) };
}

const TONE: Record<GateState["kind"], string> = {
  "not-a-specialist": "",
  pass: "border-emerald-200 bg-emerald-50 text-emerald-900 dark:border-emerald-900 dark:bg-emerald-900/20 dark:text-emerald-200",
  fail: "border-red-200 bg-red-50 text-red-900 dark:border-red-900 dark:bg-red-900/20 dark:text-red-200",
  unavailable:
    "border-slate-300 bg-slate-50 text-slate-700 dark:border-slate-700 dark:bg-slate-800/50 dark:text-slate-300",
  unchecked: "border-amber-200 bg-amber-50 text-amber-900 dark:border-amber-900 dark:bg-amber-900/20 dark:text-amber-200",
  unread: "border-amber-200 bg-amber-50 text-amber-900 dark:border-amber-900 dark:bg-amber-900/20 dark:text-amber-200",
  incomplete:
    "border-amber-200 bg-amber-50 text-amber-900 dark:border-amber-900 dark:bg-amber-900/20 dark:text-amber-200",
};

const ICON: Partial<Record<GateState["kind"], typeof CheckCircle2>> = {
  pass: CheckCircle2,
  fail: ShieldAlert,
  unavailable: PowerOff,
  unchecked: HelpCircle,
  unread: HelpCircle,
  incomplete: AlertTriangle,
};

export function CompositionGate({
  slug,
  personaVersion,
  baseVersion,
  onCancel,
  onPromote,
  promoting,
}: {
  slug: string;
  personaVersion: number;
  /** Production version of `harn:specialist-base` — HALF THE LOOKUP KEY. `null`
   *  when the page could not determine it, which must render as could-not-read
   *  rather than being sent as a default: the route coerces a missing value to
   *  0, and `(0, …)` is a triple nothing can ever have recorded. */
  baseVersion: number | null;
  onCancel: () => void;
  onPromote: () => void;
  promoting: boolean;
}) {
  const specialistId = specialistIdFromSlug(slug);
  const [state, setState] = useState<GateState>({ kind: "unread", reason: "not read yet" });
  const [loading, setLoading] = useState(false);
  const [tierA, setTierA] = useState<TierAResult | null>(null);
  const [tierABusy, setTierABusy] = useState(false);
  const [checkBusy, setCheckBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  /** Pure READ of the recorded verdict. Safe to run on mount — it records
   *  nothing. This is the ONLY gate call that may happen without a click. */
  const load = useCallback(async () => {
    if (!specialistId) return;
    // Refuse to guess half the key. Querying with a default would return a
    // confident "not yet checked" for a triple that cannot exist.
    const missingBase = requireBaseVersion(baseVersion);
    if (missingBase) {
      setState(selectGateState(slug, missingBase));
      return;
    }
    setLoading(true);
    try {
      const v = await getCompositionVerdict(specialistId, personaVersion, baseVersion as number);
      setState(selectGateState(slug, { ok: true, value: v }));
    } catch (e) {
      setState(selectGateState(slug, errShape(e)));
    } finally {
      setLoading(false);
    }
  }, [slug, specialistId, personaVersion, baseVersion]);

  useEffect(() => {
    setTierA(null);
    setActionError(null);
    void load();
  }, [load]);

  if (!specialistId) return null;

  const blocked = blocksPromotion(state);
  const reason = blockReason(state);
  const Icon = ICON[state.kind] ?? HelpCircle;

  return (
    <div
      className={`mb-3 rounded-md border p-3 text-sm ${TONE[state.kind]}`}
      data-testid="composition-gate"
    >
      <div className="flex items-start gap-2">
        <Icon className="mt-0.5 h-4 w-4 shrink-0" />
        <div className="min-w-0 flex-1">
          <div className="font-medium">
            ⚖ Composition gate — promoting{" "}
            <span className="font-mono">
              {slug} v{personaVersion}
            </span>
            {loading && <Loader2 className="ml-2 inline h-3 w-3 animate-spin" />}
          </div>

          {reason && (
            <p className="mt-1 text-xs" data-testid="composition-gate-reason">
              {reason}
            </p>
          )}

          {state.kind === "pass" && (
            <p className="mt-1 text-xs" data-testid="composition-gate-pass">
              Both tiers are green for this exact base + manifest + persona combination
              {state.checkedAt ? ` (checked ${new Date(state.checkedAt).toLocaleString()})` : ""}.
              {/* The server owns staleness: any component of the triple changing
                  voids the pass. The client never decides a pass is still good. */}
            </p>
          )}

          {state.kind === "pass" && state.advisories.length > 0 && (
            <ul className="mt-1.5 space-y-0.5 text-xs" data-testid="composition-gate-advisories">
              {state.advisories.map((a, i) => (
                <li key={`${a.assertion}-${i}`}>
                  {/* Advisories render but never block — §4-ter's judge criterion
                      is that advisory means purely stylistic. */}
                  <span className="font-mono font-medium">{a.assertion}</span> advisory
                  {a.detail ? ` — ${a.detail}` : ""}
                </li>
              ))}
            </ul>
          )}

          {state.kind === "fail" && state.checkedAt && (
            <p className="mt-1 text-xs opacity-90" data-testid="composition-gate-checked-at">
              {/* A fact, never a verdict: the recorded result GOVERNS until the
                  triple changes, so age does not weaken it — and the judge is
                  non-deterministic on marginal passages, which is why an
                  operator wants to know which draw they are looking at. */}
              Recorded {new Date(state.checkedAt).toLocaleString()}.
            </p>
          )}

          {(state.kind === "fail" || state.kind === "incomplete") && (
            <div className="mt-2 space-y-1.5 text-xs" data-testid="composition-gate-findings">
              {state.tierA.map((f, i) => (
                <div key={`a-${f.assertion}-${i}`} className="font-mono">
                  <span className="font-medium">{f.assertion}</span> {f.verdict}
                  {f.offset !== undefined && ` @ char ${f.offset}`}
                  {f.excerpt && <span className="opacity-80"> — “{f.excerpt}”</span>}
                </div>
              ))}
              {state.tierB.map((f, i) => (
                <div key={`b-${f.assertion}-${i}`}>
                  <span className="font-mono font-medium">{f.assertion}</span>{" "}
                  <span className="font-mono">{f.severity}</span>
                  {f.rationale && <> — {f.rationale}</>}
                  {/* Every finding quotes BOTH sides; lexserver validates the
                      quotes substring-match their artifact before recording, so
                      showing them verbatim is showing proven localization. */}
                  {f.personaQuote && (
                    <div className="mt-0.5 pl-3 opacity-90">
                      persona: <span className="font-mono">“{f.personaQuote}”</span>
                    </div>
                  )}
                  {f.counterpartQuote && (
                    <div className="pl-3 opacity-90">
                      {f.counterpartSource ?? "counterpart"}:{" "}
                      <span className="font-mono">“{f.counterpartQuote}”</span>
                    </div>
                  )}
                </div>
              ))}
              {/* An empty tierB with no reason reads as "the judge found
                  nothing". lexserver ships the reason for exactly that. */}
              {state.tierB.length === 0 && state.tierBSkipped && (
                <div className="opacity-80" data-testid="composition-gate-tierb-skipped">
                  Judge not run — {state.tierBSkipped}
                </div>
              )}
            </div>
          )}

          {tierA && (
            <div className="mt-2 text-xs" data-testid="composition-gate-tiera">
              Structural check: {tierA.pass ? "PASS" : "FAIL"} · {tierA.findings.length} finding
              {tierA.findings.length === 1 ? "" : "s"} · {tierA.reservedRegions.length} reserved
              regions derived from the current base.
              <span className="opacity-80">
                {" "}
                Nothing was recorded — this does not open the gate on its own.
              </span>
            </div>
          )}

          {actionError && (
            <p className="mt-2 text-xs font-medium" data-testid="composition-gate-action-error">
              {actionError}
            </p>
          )}

          <div className="mt-2.5 flex flex-wrap items-center gap-2">
            {!blocked && (
              <button
                type="button"
                onClick={onPromote}
                disabled={promoting}
                className="rounded-md bg-emerald-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-emerald-700 disabled:opacity-50"
                data-testid="composition-gate-promote"
              >
                Set v{personaVersion} active
              </button>
            )}

            <button
              type="button"
              onClick={async () => {
                setTierABusy(true);
                setActionError(null);
                try {
                  setTierA(await runTierA(specialistId, personaVersion));
                } catch (e) {
                  setActionError(errShape(e).error);
                } finally {
                  setTierABusy(false);
                }
              }}
              disabled={tierABusy}
              className="rounded-md border border-current px-3 py-1.5 text-xs font-medium disabled:opacity-50"
              title="Deterministic structural assertions only. Records nothing, so it is safe to run on a live persona."
              data-testid="composition-gate-run-tiera"
            >
              {tierABusy ? "Checking…" : "Structural check only"}
            </button>

            <button
              type="button"
              onClick={async () => {
                setCheckBusy(true);
                setActionError(null);
                try {
                  await runCompositionCheck(specialistId, personaVersion);
                } catch (e) {
                  // NOT "the check failed" — the judge can outlive the request
                  // and records its verdict server-side either way.
                  setActionError(judgeRequestFailureNotice(errShape(e).error));
                } finally {
                  // Re-read UNCONDITIONALLY: the recorded verdict is the
                  // authority, and a request that never returned may still have
                  // produced one. Reading it is free and cannot record anything.
                  await load();
                  setCheckBusy(false);
                }
              }}
              disabled={checkBusy}
              className="rounded-md border border-current px-3 py-1.5 text-xs font-medium disabled:opacity-50"
              // The label says what it does because of what it does: this RECORDS
              // a verdict, and a recorded pass is the thing the gate consults.
              title="Runs the structural checks and the LLM judge, and RECORDS the verdict for this base + manifest + persona combination. A recorded pass is what permits promotion — treat it as an approval you are signing."
              data-testid="composition-gate-run-check"
            >
              {checkBusy ? "Running the judge…" : "Run full check + record verdict"}
            </button>

            <button
              type="button"
              onClick={onCancel}
              className="px-2 py-1.5 text-xs underline opacity-80"
              data-testid="composition-gate-cancel"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
